import ImageIcon from "@mui/icons-material/Image";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Switch } from "@mui/material";
import {
  Table as MuiTable,
  TableBody,
  TableContainer,
  TableRow,
  Menu,
  MenuItem,
  IconButton,
  TableCell,
  Box,
  Pagination,
  Chip,
  Select,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import React, { useMemo, useState, JSX } from "react";
import { v4 as uuidv4 } from "uuid";

import { CustomField } from "0.Mine/3.Interfaces/Client";
import borders from "assets/theme/base/borders";
import typography from "assets/theme/base/typography";

import { SafeArgonBox, SafeArgonTypography } from "../../4.Argon/ArgonComponents";

// @mui material components

// Import the ImageDropzone component
import { ImageDropzone } from "./ImageDropzone";

// Base styles

// Define interfaces for props and other types
interface RowAction {
  name: string;
  onClick: (rowData: any) => void;
}

// Add new interfaces for image column configuration
interface ImageColumnConfig {
  field: string; // Field name in data that contains image URL
  width?: number | string; // Optional width for the image column
  height?: number | string; // Optional height for the image cell
  editable?: boolean; // Whether the image can be edited
  onImageUpdate?: (rowData: any, file: File) => Promise<void>; // Callback when image is updated
}

interface PaginationConfig {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
}

interface GenericTableProps {
  columns: string[];
  data: Record<string, any>[];
  columnLabels?: Record<string, string>;
  rowActions?: RowAction[];
  noDataMessage?: string;
  imageColumn?: ImageColumnConfig;
  pagination?: PaginationConfig | number; // Support both old and new pagination
  defineTag?: string;
  customFields?: CustomField[];
  enableToggleColumn?: boolean;
  toggleField?: string;
  onToggleChange?: (rowId: string | number, newValue: boolean) => void;
  onRowClick?: (rowData: Record<string, any>) => void;
  loading?: boolean;
}

interface MenuPosition {
  top: number;
  left: number;
}

const findPropertyCaseInsensitive = (obj: any, propertyName: string): any => {
  if (!obj || typeof obj !== "object") return undefined;

  // Direct match (for performance)
  if (obj[propertyName] !== undefined) {
    return obj[propertyName];
  }

  // Case insensitive search
  const lowerPropertyName = propertyName.toLowerCase();
  const key = Object.keys(obj).find((k) => k.toLowerCase() === lowerPropertyName);

  return key !== undefined ? obj[key] : undefined;
};

function GenericTable({
  columns = [],
  columnLabels = {},
  data = [],
  rowActions = [],
  noDataMessage = "No data available",
  imageColumn = null,
  pagination,
  defineTag,
  customFields = [],
  enableToggleColumn = false,
  toggleField,
  onToggleChange,
  onRowClick,
  loading = false,
}: GenericTableProps): JSX.Element {
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeRowData, setActiveRowData] = useState<Record<string, any> | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({ top: 0, left: 0 });
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({});

  // State for image upload operations
  const [uploadingForRowId, setUploadingForRowId] = useState<string | number | null>(null);

  const [currentPage, setCurrentPage] = useState(1);

  // Handle both pagination types
  const isPaginationConfig = pagination && typeof pagination === "object";
  const rowsPerPage = isPaginationConfig
    ? pagination.pageSize
    : typeof pagination === "number"
    ? pagination
    : data.length;
  const totalPages = isPaginationConfig
    ? Math.ceil(pagination.total / pagination.pageSize)
    : Math.ceil(data.length / (typeof pagination === "number" ? pagination : data.length));

  // Data slice for current page - only used for client-side pagination
  const paginatedData = useMemo(() => {
    if (isPaginationConfig) {
      return data; // Server-side pagination, use data as is
    }
    const start = (currentPage - 1) * (typeof pagination === "number" ? pagination : data.length);
    const end = start + (typeof pagination === "number" ? pagination : data.length);
    return data.slice(start, end);
  }, [data, currentPage, pagination, isPaginationConfig]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    if (isPaginationConfig) {
      pagination.onPageChange(value - 1);
    } else {
      setCurrentPage(value);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: Record<string, any>
  ): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      left: rect.left,
      top: rect.bottom + window.scrollY,
    });
    setMenuAnchor(event.currentTarget);
    setActiveRowData(row);
  };

  const handleMenuClose = (): void => {
    setMenuAnchor(null);
    setActiveRowData(null);
  };

  const handleActionClick = (action: RowAction): void => {
    if (action.onClick && activeRowData) {
      action.onClick(activeRowData);
    }
    handleMenuClose();
  };

  // New handler for image updates
  const handleImageUpdate = async (rowData: any, file: File): Promise<void> => {
    if (imageColumn?.onImageUpdate) {
      try {
        const rowId = rowData?.id || null;
        console.log(rowId, file);
        setUploadingForRowId(rowId);
        await imageColumn.onImageUpdate(rowId, file);
      } catch (error) {
        console.error("Error updating image:", error);
      } finally {
        setUploadingForRowId(null);
      }
    }
  };

  // Check if we should add the image column
  const hasImageColumn = Boolean(imageColumn && imageColumn.field);

  const baseColumns = hasImageColumn ? ["image", ...columns] : columns;
  const customFieldKeys = customFields.map((f) => `customField_${f.id}`);
  const allColumns = [...baseColumns, ...customFieldKeys];
  const actionColCount = rowActions && rowActions.length > 0 ? 1 : 0;

  // Render table header from the list of column names plus image column if configured
  const renderHeader = Array.isArray(allColumns)
    ? allColumns.map((col, index) => {
        // Skip rendering a header for the image column
        if (col === "image") {
          return (
            <SafeArgonBox
              key="image-column-header"
              component="th"
              pt={1.5}
              pb={1.25}
              pl={3}
              pr={1}
              textAlign="center"
              fontSize={size.xxs}
              fontWeight={fontWeightBold}
              color="secondary"
              opacity={0.7}
              sx={({ palette: { light } }) => ({
                borderBottom: `${borderWidth[1]} solid ${light.main}`,
                width: imageColumn?.width || 80,
              })}
            >
              IMAGE
            </SafeArgonBox>
          );
        }

        if (col.startsWith("customField_")) {
          const id = col.split("_")[1];
          const field = customFields.find((f) => f.id === id);
          const paddingLeft = index === 0 ? 3 : 1;
          const paddingRight = index === allColumns.length - actionColCount - 1 ? 3 : 1;
          return (
            <SafeArgonBox
              key={col}
              component="th"
              pt={1.5}
              pb={1.25}
              pl={paddingLeft}
              pr={paddingRight}
              textAlign="left"
              fontSize={size.xxs}
              fontWeight={fontWeightBold}
              color="secondary"
              opacity={0.7}
              sx={({ palette: { light } }) => ({
                borderBottom: `${borderWidth[1]} solid ${light.main}`,
              })}
            >
              {field ? field.name.toUpperCase() : ""}
            </SafeArgonBox>
          );
        }

        // Regular column headers
        const paddingLeft = index === 0 ? 3 : 1;
        const paddingRight = index === allColumns.length - 1 ? 3 : 1;
        return (
          <SafeArgonBox
            key={col}
            component="th"
            pt={1.5}
            pb={1.25}
            pl={paddingLeft}
            pr={paddingRight}
            textAlign="left"
            fontSize={size.xxs}
            fontWeight={fontWeightBold}
            color="secondary"
            opacity={0.7}
            sx={({ palette: { light } }) => ({
              borderBottom: `${borderWidth[1]} solid ${light.main}`,
            })}
          >
            {columnLabels[col] ? columnLabels[col].toUpperCase() : col.toUpperCase()}
          </SafeArgonBox>
        );
      })
    : [];

  //activate or disable workflow from table
  const toggleHeader = (
    <SafeArgonBox
      component="th"
      pt={1.5}
      pb={1.25}
      pl={3}
      pr={1}
      textAlign="center"
      fontSize={size.xxs}
      fontWeight={fontWeightBold}
      color="secondary"
      opacity={0.7}
      sx={({ palette: { light } }) => ({
        borderBottom: `${borderWidth[1]} solid ${light.main}`,
        width: 80,
      })}
    >
      STATO
    </SafeArgonBox>
  );

  // Add one more header cell for actions column if there are actions
  const completeHeader = (
    <>
      {enableToggleColumn && toggleHeader}
      {renderHeader}
      {Array.isArray(rowActions) && rowActions.length > 0 && (
        <SafeArgonBox
          component="th"
          pt={1.5}
          pb={1.25}
          pl={1}
          pr={3}
          textAlign="right"
          fontSize={size.xxs}
          fontWeight={fontWeightBold}
          color="secondary"
          opacity={0.7}
          sx={({ palette: { light } }) => ({
            borderBottom: `${borderWidth[1]} solid ${light.main}`,
          })}
        >
          AZIONI
        </SafeArgonBox>
      )}
    </>
  );

  // Render each row of the table
  const renderRows = paginatedData.map((row, rowIndex) => {
    // Use row.id if available, or fall back to the row index
    const rowKey = findPropertyCaseInsensitive(row, "id") || rowIndex;

    return (
      <TableRow
        key={`row-${rowKey}`}
        onClick={() => onRowClick && onRowClick(row)}
        sx={{
          cursor: onRowClick ? "pointer" : "default",
          "&:hover": onRowClick
            ? {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              }
            : {},
        }}
      >
        {enableToggleColumn && (
          <TableCell
            align="center"
            onClick={(e) => {
              e.stopPropagation();
              const currentValue = !!findPropertyCaseInsensitive(row, toggleField || "enabled");
              const newValue = !currentValue;
              setToggleStates((prev) => ({
                ...prev,
                [rowKey]: newValue,
              }));
              onToggleChange?.(rowKey, newValue);
            }}
            sx={{ cursor: "pointer" }}
          >
            <Switch
              checked={!!findPropertyCaseInsensitive(row, toggleField || "enabled")}
              onClick={(e) => e.stopPropagation()}
              color="primary"
            />
          </TableCell>
        )}
        {Array.isArray(allColumns) &&
          allColumns.map((col) => {
            // Special handling for image column
            if (col === "image" && imageColumn) {
              const imageUrl = findPropertyCaseInsensitive(row, imageColumn.field);
              const isUploading = uploadingForRowId === rowKey;

              return (
                <TableCell
                  key={uuidv4()}
                  sx={{
                    height: "52px",
                    padding: "6px 16px",
                    borderBottom: (theme) => `${borderWidth[1]} solid ${theme.palette.grey[200]}`,
                    maxWidth: "200px",
                    "& .MuiTypography-root": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                      display: "block",
                    },
                  }}
                >
                  <Tooltip title={imageUrl || "No Image"} arrow>
                    <SafeArgonTypography variant="button" fontWeight="regular" color="secondary">
                      {imageUrl || "No Image"}
                    </SafeArgonTypography>
                  </Tooltip>
                </TableCell>
              );
            }

            if (defineTag && col === defineTag) {
              const raw = findPropertyCaseInsensitive(row, col);
              const tags: string[] = Array.isArray(raw)
                ? raw
                : typeof raw === "string"
                ? raw
                    .split(",")
                    .map((t: string) => t.trim())
                    .filter(Boolean)
                : [];
              return (
                <TableCell
                  key={uuidv4()}
                  sx={{
                    height: "52px",
                    padding: "6px 16px",
                    borderBottom: (theme) => `${borderWidth[1]} solid ${theme.palette.grey[200]}`,
                    maxWidth: "200px",
                    "& .MuiTypography-root": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                      display: "block",
                    },
                  }}
                >
                  <Tooltip title={tags.join(", ")} arrow>
                    <SafeArgonTypography variant="button" fontWeight="regular" color="secondary">
                      {tags.join(", ")}
                    </SafeArgonTypography>
                  </Tooltip>
                </TableCell>
              );
            }

            if (col.startsWith("customField_")) {
              const id = col.split("_")[1];
              const raw = row.customFields?.[id];
              const value = raw != null ? raw : "-";
              return (
                <TableCell
                  key={uuidv4()}
                  sx={{
                    height: "52px",
                    padding: "6px 16px",
                    borderBottom: (theme) => `${borderWidth[1]} solid ${theme.palette.grey[200]}`,
                    maxWidth: "200px",
                    "& .MuiTypography-root": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                      display: "block",
                    },
                  }}
                >
                  <Tooltip title={value !== "-" ? value : "No Value"} arrow>
                    <SafeArgonTypography
                      variant="button"
                      fontWeight="regular"
                      color={value !== "-" ? "secondary" : "textSecondary"}
                    >
                      {value}
                    </SafeArgonTypography>
                  </Tooltip>
                </TableCell>
              );
            }

            // Regular data cell
            const cellContent =
              findPropertyCaseInsensitive(row, col) !== undefined
                ? String(findPropertyCaseInsensitive(row, col))
                : "—";

            return (
              <TableCell
                key={uuidv4()}
                sx={{
                  height: "52px",
                  padding: "6px 16px",
                  borderBottom: (theme) => `${borderWidth[1]} solid ${theme.palette.grey[200]}`,
                  maxWidth: "200px",
                  "& .MuiTypography-root": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                    display: "block",
                  },
                }}
              >
                <Tooltip title={cellContent} arrow>
                  <SafeArgonTypography variant="button" fontWeight="regular" color="secondary">
                    {cellContent}
                  </SafeArgonTypography>
                </Tooltip>
              </TableCell>
            );
          })}

        {/* Action column */}
        {Array.isArray(rowActions) && rowActions.length > 0 && (
          <TableCell
            sx={{
              height: "52px",
              padding: "6px 16px",
              borderBottom: (theme) => `${borderWidth[1]} solid ${theme.palette.grey[200]}`,
              textAlign: "right",
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, row);
              }}
              size="small"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
    );
  });

  const actionMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={Boolean(menuAnchor)}
      onClose={handleMenuClose}
      anchorReference="anchorPosition"
      anchorPosition={menuAnchor ? menuPosition : undefined}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        elevation: 3,
        sx: {
          overflow: "visible",
          mt: 1,
        },
      }}
      MenuListProps={{
        sx: { py: 1 },
      }}
    >
      {Array.isArray(rowActions) &&
        rowActions.map((action) => (
          <MenuItem key={action.name} onClick={() => handleActionClick(action)}>
            {action.name}
          </MenuItem>
        ))}
    </Menu>
  );

  const hasData = Array.isArray(data) && data.length > 0;

  return useMemo(
    () => (
      <TableContainer>
        {loading || !hasData ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={48} />
                  <SafeArgonTypography variant="h6" color="text" fontWeight="regular">
                    Caricamento in corso...
                  </SafeArgonTypography>
                </>
              ) : (
                <>
                  <InfoOutlinedIcon
                    color="info"
                    sx={{
                      fontSize: 48,
                      opacity: 0.6,
                    }}
                  />
                  <SafeArgonTypography variant="h6" color="text" fontWeight="regular">
                    {noDataMessage}
                  </SafeArgonTypography>
                </>
              )}
            </Box>
          </Box>
        ) : (
          <>
            <MuiTable>
              <thead>
                <TableRow>{completeHeader}</TableRow>
              </thead>
              <TableBody>{renderRows}</TableBody>
            </MuiTable>
            {pagination && totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                py={2}
                sx={{
                  borderTop: "1px solid",
                  borderColor: "divider",
                  width: "100%",
                }}
              >
                <Pagination
                  count={totalPages}
                  page={isPaginationConfig ? pagination.page + 1 : currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
        {actionMenu}
      </TableContainer>
    ),
    [
      columns,
      data,
      menuAnchor,
      defineTag,
      menuPosition,
      rowActions,
      noDataMessage,
      uploadingForRowId,
      imageColumn,
      pagination,
      loading,
      currentPage,
      paginatedData,
      totalPages,
      isPaginationConfig,
      enableToggleColumn,
    ]
  );
}

export default GenericTable;
